import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

interface SwarmNode {
  id: string;
  name: string;
  role: string;
  type: 'coordinator' | 'worker' | 'reviewer' | 'specialist';
  color: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface SwarmLink {
  source: string | SwarmNode;
  target: string | SwarmNode;
  label: string;
}

interface SwarmVisualizationProps {
  nodes: SwarmNode[];
  links: SwarmLink[];
  onNodeClick?: (node: SwarmNode) => void;
  className?: string;
}

export default function SwarmVisualization({ nodes, links, onNodeClick, className = '' }: SwarmVisualizationProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<SwarmNode, SwarmLink> | null>(null);
  const [selectedNode, setSelectedNode] = useState<SwarmNode | null>(null);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    const width = svgRef.current.clientWidth || 800;
    const height = svgRef.current.clientHeight || 500;

    svg.selectAll('*').remove();

    const g = svg.append('g');

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    const defs = svg.append('defs');
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 30)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#94a3b8');

    const nodeData: SwarmNode[] = nodes.map((n) => ({ ...n }));
    const linkData: SwarmLink[] = links.map((l) => ({ ...l }));

    const simulation = d3.forceSimulation<SwarmNode>(nodeData)
      .force('link', d3.forceLink<SwarmNode, SwarmLink>(linkData).id((d) => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));

    simulationRef.current = simulation;

    const link = g.append('g')
      .selectAll('line')
      .data(linkData)
      .join('line')
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', 'url(#arrowhead)');

    const linkLabel = g.append('g')
      .selectAll('text')
      .data(linkData)
      .join('text')
      .attr('font-size', 10)
      .attr('fill', '#64748b')
      .attr('text-anchor', 'middle')
      .text((d) => d.label);

    const node = g.append('g')
      .selectAll<SVGGElement, SwarmNode>('g')
      .data(nodeData)
      .join('g')
      .call(d3.drag<SVGGElement, SwarmNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      )
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedNode(d);
        onNodeClick?.(d);
      });

    node.append('circle')
      .attr('r', 24)
      .attr('fill', (d) => d.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))');

    node.append('text')
      .attr('dy', 4)
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .style('pointer-events', 'none')
      .text((d) => d.name.split(' ').map((w) => w[0]).join(''));

    node.append('text')
      .attr('dy', -34)
      .attr('text-anchor', 'middle')
      .attr('fill', '#1e293b')
      .attr('font-size', 11)
      .attr('font-weight', 500)
      .style('pointer-events', 'none')
      .text((d) => d.name);

    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as SwarmNode).x!)
        .attr('y1', (d) => (d.source as SwarmNode).y!)
        .attr('x2', (d) => (d.target as SwarmNode).x!)
        .attr('y2', (d) => (d.target as SwarmNode).y!);

      linkLabel
        .attr('x', (d) => ((d.source as SwarmNode).x! + (d.target as SwarmNode).x!) / 2)
        .attr('y', (d) => ((d.source as SwarmNode).y! + (d.target as SwarmNode).y!) / 2 - 6);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
    };
  }, [nodes, links, onNodeClick]);

  return (
    <div className={`relative ${className}`}>
      <svg
        ref={svgRef}
        className="w-full h-full bg-gray-900 rounded-xl"
        style={{ minHeight: 500 }}
      />
      {selectedNode && (
        <div className="absolute top-4 right-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 w-64 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
              style={{ backgroundColor: selectedNode.color }}
            >
              {selectedNode.name.split(' ').map((w) => w[0]).join('')}
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">{selectedNode.name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{selectedNode.type}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{selectedNode.role}</p>
          <button
            className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
            onClick={() => setSelectedNode(null)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
